import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import LessonCounter from "@/components/LessonCounter";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Lock, BookOpen, Newspaper, Sparkles, Mountain,
  Eye, FileText, Layers, ChevronRight,
} from "lucide-react";

/**
 * SneakPeek — the public teaser page that replaces the old public
 * /courses and /articles listings. Two CTAs at every scroll depth:
 *   • "Not sure? Make a free account"    → /register
 *   • "Ready to take the climb?"         → /pricing
 *
 * The page reads the same public endpoints (/api/courses,
 * /api/articles) so the tease always reflects the real curriculum —
 * chapter titles, article headlines, and read-times — but the bodies
 * are hidden behind a gate.
 */
export default function SneakPeeak() {
  const { t, i18n } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    const lang = i18n.language?.slice(0, 2) || "en";
    api.get("/courses", { params: { language: lang } })
      .then((r) => setCourses(r.data || []))
      .catch(() => setCourses([]));
    api.get("/articles", { params: { language: lang } })
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]));
  }, [i18n.language]);

  // Catalog-wide stats are counted on the English (master) catalog so every language shows the same numbers.
  const [catalog, setCatalog] = useState({ courses: null, articles: null });
  useEffect(() => {
    Promise.all([
      api.get("/courses", { params: { language: "en" } }),
      api.get("/articles", { params: { language: "en" } }),
    ]).then(([c, a]) => setCatalog({ courses: (c.data || []).length, articles: (a.data || []).length })).catch(() => {});
  }, []);

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="sneak-peek-root">

      {/* ─── HERO ────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_20%_30%,rgba(30,64,175,0.55),transparent_55%),radial-gradient(circle_at_80%_70%,rgba(245,158,11,0.4),transparent_50%)]" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-24 lg:py-32">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/30 px-4 py-1.5 text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold mb-6">
            <Eye size={12} /> {t("sneakPeekUi.sneakPeekBadge")}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            {t("sneakPeekUi.heroTitle")}<br />
            <span className="text-amber-400">{t("sneakPeekUi.heroTitleHighlight")}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            {t("sneakPeekUi.heroDescription")}
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/onboarding?src=preview">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8 shadow-2xl shadow-amber-500/20">
                {t("sneakPeekUi.heroCtaUnsure")}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/pricing?src=preview">
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-8 border-white/25 text-white bg-transparent hover:bg-white/10 hover:border-white"
              >
                <Mountain size={16} className="mr-2" /> {t("sneakPeekUi.heroCtaReady")}
              </Button>
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
            <div data-testid="sneak-peek-stat-lessons"><LessonCounter dark /></div>
            {[
              { n: catalog.courses ?? "—", labelKey: "sneakPeekUi.statCourses" },
              { n: catalog.articles ?? "—", labelKey: "sneakPeekUi.statFieldNotes" },
              { n: 3, labelKey: "sneakPeekUi.statLanguages" },
            ].map((s) => (
              <div key={s.labelKey}>
                <div className="font-display font-black text-4xl sm:text-5xl text-white tabular-nums">{s.n}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mt-1">{t(s.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CURRICULUM TEASER ─────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">{t("sneakPeekUi.curriculumSectionLabel")}</div>
            <h2 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
              {t("sneakPeekUi.curriculumTitle")}
            </h2>
          </div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
            <Lock size={12} /> {t("sneakPeekUi.curriculumLockBadge")}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {courses.slice(0, 3).map((c, i) => (
            <div
              key={c.id || i}
              data-testid="sneak-peek-course-card"
              className="relative bg-white border border-slate-200 rounded-2xl p-6 flex flex-col hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-lg bg-blue-100 text-blue-800 flex items-center justify-center">
                  <Layers size={18} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-blue-800 font-semibold">
                    {t("sneakPeekUi.trackLabel", { number: String(i + 1).padStart(2, "0") })}
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-900 leading-tight tracking-tight">
                    {c.title}
                  </h3>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                {c.description || t("sneakPeekUi.courseDefaultDescription")}
              </p>

              {/* Chapter tease list */}
              <ul className="mt-5 space-y-2 flex-1">
                {(c.chapters || Array.from({ length: 4 })).slice(0, 4).map((ch, idx) => {
                  const label = typeof ch === "object" ? (ch.title || t("sneakPeekUi.chapterLabel", { number: idx + 1 })) : t("sneakPeekUi.chapterLabel", { number: idx + 1 });
                  const isLocked = idx > 0;
                  return (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      {isLocked
                        ? <Lock size={13} className="text-slate-400 shrink-0" />
                        : <ChevronRight size={13} className="text-blue-800 shrink-0" />}
                      <span className={isLocked ? "text-slate-400" : "text-slate-800 font-medium"}>
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[11px] text-slate-500 font-mono">
                  {c.lesson_count || "—"} {t("sneakPeekUi.lessonsLabel")} · {c.tier || "free"}+
                </span>
                <Link
                  to="/register?src=preview-course"
                  className="text-xs font-semibold text-blue-800 hover:text-blue-700 inline-flex items-center gap-1"
                >
                  {t("sneakPeekUi.unlockFreeLink")} <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── SPLIT CTA ─────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 pb-8">
        <div className="relative overflow-hidden rounded-3xl border border-amber-500/25 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white p-10 sm:p-14">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold mb-3">
                {t("sneakPeekUi.pathACurious")}
              </div>
              <h3 className="font-display font-bold text-3xl leading-tight tracking-tight text-white">
                {t("sneakPeekUi.pathATitle")}
              </h3>
              <p className="mt-3 text-slate-300 leading-relaxed max-w-md">
                {t("sneakPeekUi.pathADescription")}
              </p>
              <Link to="/onboarding?src=preview-splitcta" className="mt-6 inline-block">
                <Button size="lg" className="bg-white text-slate-950 hover:bg-slate-100 font-semibold h-12 px-8">
                  {t("sneakPeekUi.pathAButton")} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
            <div className="lg:border-l lg:border-white/10 lg:pl-10">
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold mb-3">
                {t("sneakPeekUi.pathBCommitted")}
              </div>
              <h3 className="font-display font-bold text-3xl leading-tight tracking-tight text-white">
                {t("sneakPeekUi.pathBTitle")}
              </h3>
              <p className="mt-3 text-slate-300 leading-relaxed max-w-md">
                {t("sneakPeekUi.pathBDescription")}
              </p>
              <Link to="/pricing?src=preview-splitcta" className="mt-6 inline-block">
                <Button
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8"
                >
                  <Mountain size={16} className="mr-2" /> {t("sneakPeekUi.pathBButton")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ARTICLE TEASER ────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-24">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">{t("sneakPeekUi.fieldNotesSectionLabel")}</div>
          <h2 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
            {t("sneakPeekUi.fieldNotesTitle")}
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            {t("sneakPeekUi.fieldNotesDescription")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.slice(0, 3).map((a) => (
            <article
              key={a.id}
              data-testid="sneak-peek-article-card"
              className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              {a.cover_image && (
                <div className="aspect-video bg-slate-100 overflow-hidden">
                  <img src={a.cover_image} alt={a.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-5 flex flex-col flex-1">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-blue-800">
                  <FileText size={11} /> {a.category || t("sneakPeekUi.fieldNoteCategory")}
                </div>
                <h3 className="mt-2 font-display font-bold text-lg text-slate-900 leading-tight tracking-tight">
                  {a.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2 leading-relaxed">
                  {a.excerpt}
                </p>

                {/* Fake locked body preview */}
                <div className="mt-4 relative flex-1">
                  <div className="space-y-1.5">
                    {[90, 96, 82, 88, 65].map((w, i) => (
                      <div key={i} className="h-2 bg-slate-100 rounded-full" style={{ width: `${w}%` }} />
                    ))}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-b from-white/0 via-white/60 to-white flex items-end justify-center pb-1">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      <Lock size={9} /> {t("sneakPeekUi.freeSignupUnlocks")}
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-[11px] text-slate-500 font-medium">
                  <span>{a.read_time || t("sneakPeekUi.defaultReadTime")}</span>
                  <Link to="/register?src=preview-article" className="text-blue-800 font-semibold hover:text-blue-700 inline-flex items-center gap-1">
                    {t("sneakPeekUi.readFreeLink")} <ArrowRight size={11} />
                  </Link>
                </div>
              </div>
            </article>
          ))}

          {articles.length === 0 && (
            <div className="col-span-full text-center py-16 bg-white border border-dashed border-slate-300 rounded-2xl">
              <Newspaper size={28} className="mx-auto text-slate-400 mb-3" />
              <p className="text-sm text-slate-600 font-semibold">{t("sneakPeekUi.fieldNotesComingSoon")}</p>
            </div>
          )}
        </div>
      </section>

      {/* ─── FINAL PUSH ────────────────────────────────────────────── */}
      <section className="bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_50%_50%,rgba(245,158,11,0.35),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 py-24 text-center">
          <Sparkles size={28} className="mx-auto text-amber-400 mb-4" />
          <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight tracking-tighter">
            {t("sneakPeekUi.finalTitle")}<br />
            <span className="text-amber-400">{t("sneakPeekUi.finalTitleHighlight")}</span>
          </h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">
            {t("sneakPeekUi.finalDescription")}
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/register?src=preview-footer">
              <Button size="lg" className="bg-white text-slate-950 hover:bg-slate-100 font-semibold h-12 px-8">
                <BookOpen size={16} className="mr-2" /> {t("sneakPeekUi.finalCtaStartFree")}
              </Button>
            </Link>
            <Link to="/pricing?src=preview-footer">
              <Button
                size="lg"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8"
              >
                <Mountain size={16} className="mr-2" /> {t("sneakPeekUi.finalCtaMembership")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
